import {IconButton} from '@dropbox/dig-components/buttons';
import {Chip} from '@dropbox/dig-components/chip';
import {DatePickerInput} from '@dropbox/dig-components/date_picker';
import {TextInput} from '@dropbox/dig-components/text_fields';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Truncate} from '@dropbox/dig-components/truncate';
import {Typeahead, WrapperRef} from '@dropbox/dig-components/typeahead';
import {atoms, Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {CloseLine, CopyLine, PersonLine, SearchLine} from '@dropbox/dig-icons/assets';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {snackbarAtom} from 'atoms/snackbar';
import {Employee, EmployeeLdap} from 'client';
import {Avatar} from 'components/DSYS/Avatar';
import {EmployeeButton} from 'components/DSYS/EmployeeButton';
import {PersonHoverCard} from 'components/DSYS/HoverCard';
import {useSearches} from 'hooks/useEmployee';
import {useOutsideClick} from 'hooks/useOutsideClick';
import {t} from 'i18next';
import {useAtomValue, useSetAtom} from 'jotai';
import {useCallback, useEffect, useId, useRef, useState} from 'react';
import {getFrontendUrl} from 'utilities';
import {toCalendarDate, toEODISOStringFromDate} from 'views/admin/utils';

import {toShortDate} from './utils';

const eventTargetIsInsideSelector = (target: EventTarget | null, selector: string) => {
  if (!(target instanceof Node)) return false;
  return Array.from(document.querySelectorAll(selector)).some((element) =>
    element.contains(target)
  );
};

const employeeTypeaheadTextStyle: React.CSSProperties = {
  wordBreak: 'normal',
  overflowWrap: 'break-word',
  hyphens: 'manual',
};

const useInlineEditorDismiss = ({
  enabled,
  ref,
  ignoreSelectors,
  onDismiss,
}: {
  enabled: boolean;
  ref: React.RefObject<HTMLElement>;
  ignoreSelectors: string[];
  onDismiss: () => void;
}) => {
  useEffect(() => {
    if (!enabled) return;

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target;
      if (ref.current?.contains(target as Node)) return;
      if (ignoreSelectors.some((selector) => eventTargetIsInsideSelector(target, selector))) return;
      onDismiss();
    };

    document.addEventListener('mousedown', handleMouseDown, true);
    return () => document.removeEventListener('mousedown', handleMouseDown, true);
  }, [enabled, ignoreSelectors, onDismiss, ref]);
};

export const DatePickerEditor = ({
  error,
  disabled,
  date,
  setDate,
  placeholder,
  disableNoDateSelection,
}: {
  error?: boolean;
  disabled?: boolean;
  date: string | null;
  setDate: (date: string | null) => void;
  placeholder?: string;
  disableNoDateSelection?: boolean;
}) => {
  const inputId = useId().replace(/:/g, '');

  const handleChange = (newDate: Date | null) => {
    if (disableNoDateSelection && !newDate) return;
    setDate(toEODISOStringFromDate(newDate));
  };

  return (
    <Box style={{display: 'inline-block', width: '100%'}}>
      <DatePickerInput
        disabled={disabled}
        id={`choose-date-${inputId}`}
        autocomplete="off"
        isInvalid={error}
        isTransparent
        size="small"
        placeholder={placeholder ?? t('eta')}
        format={{
          stringToDate: (input) => {
            const trimmedInput = input.trim();
            if (!trimmedInput) {
              return new Date(Number.NaN);
            }
            const inputWithYear = /\d{4}$/.test(trimmedInput)
              ? trimmedInput
              : `${trimmedInput}, ${new Date().getFullYear()}`;
            return new Date(inputWithYear);
          },
          dateToString: (selectedDate) => (selectedDate ? toShortDate(selectedDate) : ''),
        }}
        value={date ? toCalendarDate(date) : null}
        onChange={handleChange}
      />
    </Box>
  );
};

export const EmployeeSelect = ({
  isInvalid,
  selection,
  setSelection,
  disabled,
  chipContentMaxWidth,
  inputClassName,
  startEditing = false,
  onDismiss,
  resultsWidth = 400,
  resultsContainerStyle,
  allowEmptySelection = false,
}: {
  isInvalid?: boolean;
  selection?: EmployeeLdap;
  setSelection: (employee?: Employee) => void;
  disabled?: boolean;
  chipContentMaxWidth?: number;
  inputClassName?: string;
  startEditing?: boolean;
  onDismiss?: () => void;
  resultsWidth?: number | string;
  resultsContainerStyle?: React.CSSProperties;
  allowEmptySelection?: boolean;
}) => {
  const typeaheadRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(startEditing);
  const [shouldClearInput, setShouldClearInput] = useState(startEditing);
  const {reportingLine} = useAtomValue(loggedInEmployeeAtom);

  useInlineEditorDismiss({
    enabled: isEditing,
    ref: typeaheadRef,
    ignoreSelectors: ['.dig-Typeahead-container'],
    onDismiss: () => {
      setIsEditing(false);
      onDismiss?.();
    },
  });

  useOutsideClick(typeaheadRef, () => {
    setIsEditing(false);
  });

  const handleSelection = (employee: Employee) => {
    setSelection(employee);
    setIsEditing(false);
    setShouldClearInput(false);
    onDismiss?.();
  };

  const handleDelete = () => {
    setSelection(undefined);
    setIsEditing(true);
    setShouldClearInput(true);
  };

  const handleClear = () => {
    setSelection(undefined);
    setIsEditing(false);
    setShouldClearInput(true);
    onDismiss?.();
  };

  const handleChipClick = () => {
    setIsEditing(true);
    setShouldClearInput(false);
  };

  return isEditing || !selection || disabled ? (
    <Box
      ref={typeaheadRef}
      className={inputClassName}
      style={{display: 'inline-block', width: '100%'}}
    >
      <EmployeeTypeahead
        isInvalid={isInvalid}
        selection={shouldClearInput ? undefined : selection}
        onSingleSelect={handleSelection}
        placeholder={reportingLine}
        autoFocus={isEditing}
        disabled={disabled}
        resultsWidth={resultsWidth}
        resultsContainerStyle={resultsContainerStyle}
        onClear={allowEmptySelection && selection ? handleClear : undefined}
      />
    </Box>
  ) : (
    <EmployeeChip
      employee={selection}
      handleChipClick={handleChipClick}
      handleDelete={handleDelete}
      chipContentMaxWidth={chipContentMaxWidth}
    />
  );
};

export const EmployeeChip = ({
  employee,
  handleChipClick,
  handleDelete,
  chipContentMaxWidth,
  isSelected,
}: {
  employee: EmployeeLdap;
  handleChipClick?: () => void;
  handleDelete?: () => void;
  chipContentMaxWidth?: number;
  isSelected?: boolean;
}) => {
  const name = employee?.name ?? t('dri');
  const chip = (
    <Chip onClick={handleChipClick} size="small" onDelete={handleDelete} isSelected={isSelected}>
      {employee ? (
        <Chip.AvatarAccessory>
          <Avatar user={employee} />
        </Chip.AvatarAccessory>
      ) : (
        <Chip.IconAccessory>
          <UIIcon src={PersonLine} />
        </Chip.IconAccessory>
      )}

      <Chip.Content>
        {chipContentMaxWidth !== undefined ? (
          <EmployeeTruncatedName name={name} maxWidth={chipContentMaxWidth} />
        ) : (
          name
        )}
      </Chip.Content>
    </Chip>
  );

  return employee?.ldap ? (
    <PersonHoverCard ldap={employee.ldap} name={name}>
      <span style={{display: 'inline-flex'}}>{chip}</span>
    </PersonHoverCard>
  ) : (
    chip
  );
};

export const MultiEmployeeSelect = ({
  selections,
  setSelections,
  disabled,
  inputClassName,
  startEditing = false,
  autoFocus = true,
  keepInputVisible = false,
  resultsPlacement,
  resultsAuto = true,
  resultsWidth = 400,
  resultsContainerStyle,
}: {
  selections: EmployeeLdap[];
  setSelections: (employees: Employee[]) => void;
  disabled?: boolean;
  inputClassName?: string;
  startEditing?: boolean;
  autoFocus?: boolean;
  keepInputVisible?: boolean;
  resultsPlacement?: React.ComponentProps<typeof Typeahead.Container>['placement'];
  resultsAuto?: boolean;
  resultsWidth?: number | string;
  resultsContainerStyle?: React.CSSProperties;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const typeaheadWrapperRef = useRef<WrapperRef>();
  const shouldFocusInputRef = useRef(autoFocus);
  const [userInputValue, setUserInputValue] = useState('');
  const [isChipSelectionActive, setIsChipSelectionActive] = useState(false);
  const [isInputVisible, setIsInputVisible] = useState(keepInputVisible || startEditing);
  const {reportingLine} = useAtomValue(loggedInEmployeeAtom);
  const setSnackbar = useSetAtom(snackbarAtom);

  useOutsideClick(containerRef, () => {
    setIsChipSelectionActive(false);
  });

  useEffect(() => {
    if (keepInputVisible) {
      setIsInputVisible(true);
      return;
    }
    if (selections.length === 0) {
      setIsInputVisible(true);
    } else {
      setIsInputVisible(false);
    }
  }, [keepInputVisible, selections.length]);

  useEffect(() => {
    if (!isInputVisible) return;
    if (!shouldFocusInputRef.current) return;
    shouldFocusInputRef.current = false;
    requestAnimationFrame(() => {
      typeaheadWrapperRef.current?.focusTrigger();
      typeaheadWrapperRef.current?.openResults({autoFocus: false});
    });
  }, [isInputVisible]);

  const searchResult = useSearches({
    ldaps: userInputValue.split(',').map((e) => (!e.includes('@') ? e : e.trim().split('@')[0])),
  });

  const employeeHints = searchResult.length ? searchResult : reportingLine;
  const selectedUserIds = new Set(selections.map((s) => s.user_id));
  const filteredHints = employeeHints.filter((e) => !selectedUserIds.has(e.user_id));

  // Refs to avoid stale closures and prevent infinite loops in paste effect
  const selectionsRef = useRef(selections);
  selectionsRef.current = selections;
  const setSelectionsRef = useRef(setSelections);
  setSelectionsRef.current = setSelections;

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUserInputValue(event.currentTarget.value);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setIsChipSelectionActive(false);
      return;
    }

    const isMod = event.metaKey || event.ctrlKey;
    if (!isMod) return;

    if (event.key === 'a' || event.key === 'A') {
      event.stopPropagation();
      if (selections.length > 0) {
        event.preventDefault();
        setIsChipSelectionActive(true);
      }
      return;
    }
    if (event.key === 'c' || event.key === 'C') {
      event.stopPropagation();
      if (isChipSelectionActive) {
        event.preventDefault();
        handleCopyEmails();
      }
      return;
    }
    if (event.key === 'v' || event.key === 'V') {
      event.stopPropagation();
    }
  };

  const handleSelection = (userId: string) => {
    const selectedEmployee = employeeHints.find((e) => e.user_id === userId);
    if (selectedEmployee && !selectedUserIds.has(selectedEmployee.user_id)) {
      setSelections([...selectionsRef.current, selectedEmployee] as Employee[]);
      setUserInputValue('');
      // Refocus input after mouse click on typeahead row so user can keep typing
      requestAnimationFrame(() => {
        typeaheadWrapperRef.current?.focusTrigger();
      });
    }
  };

  const handleRemove = (employee: EmployeeLdap) => {
    setSelections(selections.filter((s) => s.user_id !== employee.user_id) as Employee[]);
  };

  const handleOpenProfile = useCallback((employee: EmployeeLdap) => {
    window.open(`${getFrontendUrl()}people/${employee.ldap}`, '_blank', 'noopener,noreferrer');
  }, []);

  // Resolve pasted comma-separated emails/ldaps into employees.
  // We wait until ALL search queries have resolved before processing,
  // so we can match every pasted email in a single pass.
  const pastedInput = useRef('');
  useEffect(() => {
    if (!userInputValue.includes(',')) {
      pastedInput.current = '';
      return;
    }
    // Remember the first input that contained commas (the paste)
    if (!pastedInput.current) {
      pastedInput.current = userInputValue;
    }
  }, [userInputValue]);

  const allQueriesSettled = searchResult.length > 0 || !userInputValue.includes(',');
  useEffect(() => {
    if (!pastedInput.current || !userInputValue.includes(',') || !searchResult.length) return;

    const segments = pastedInput.current
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);

    const currentSelections = selectionsRef.current;
    const currentIds = new Set(currentSelections.map((s) => s.user_id));
    const found: Employee[] = [];
    const unresolved: string[] = [];

    segments.forEach((seg) => {
      const ldap = seg.includes('@') ? seg.split('@')[0] : seg;
      const match = searchResult.find((e) => e.ldap === ldap || e.name.toLowerCase() === seg);
      if (
        match &&
        !currentIds.has(match.user_id) &&
        !found.some((f) => f.user_id === match.user_id)
      ) {
        found.push(match);
      } else if (!match) {
        unresolved.push(seg);
      }
    });

    if (found.length) {
      pastedInput.current = '';
      setUserInputValue(unresolved.join(', '));
      setSelectionsRef.current([...currentSelections, ...found] as Employee[]);
    }
  }, [searchResult, userInputValue, allQueriesSettled]);

  const handleCopyEmails = useCallback(async () => {
    const emails = selections
      .map((s) => ('email' in s && s.email) || `${s.ldap}@dropbox.com`)
      .filter(Boolean)
      .join(', ');

    try {
      await navigator.clipboard.writeText(emails);
      setSnackbar({text: t('emails_copied')});
      if (!keepInputVisible && selections.length > 0) {
        setIsInputVisible(false);
      }
    } catch {
      // `navigator.clipboard.writeText` API can sometimes fail from Safari or Firefox when it is not triggered by a user action
      // this is a fallback for those cases so that user can at least manually copy the emails this way
      setSnackbar({
        text: (
          <span style={{display: 'block'}}>
            <span style={{display: 'block', marginBottom: '4px'}}>{t('error_copying_emails')}</span>
            <span
              style={{display: 'block', userSelect: 'text', cursor: 'text', wordBreak: 'break-all'}}
            >
              {emails}
            </span>
          </span>
        ),
      });
    }
  }, [keepInputVisible, selections, setSnackbar]);

  const handleTypeaheadToggle = useCallback(
    ({isOpen}: {isOpen: boolean}) => {
      if (keepInputVisible || isOpen || selections.length === 0) return;
      requestAnimationFrame(() => {
        const activeElement = document.activeElement;
        if (containerRef.current?.contains(activeElement)) return;
        setIsInputVisible(false);
      });
    },
    [keepInputVisible, selections.length]
  );

  const renderTypeaheadRow = (employee: Employee) => (
    <Typeahead.Row
      key={employee.user_id}
      value={employee.user_id}
      withTitle={<span style={employeeTypeaheadTextStyle}>{employee.name}</span>}
      withSubtitle={<span style={employeeTypeaheadTextStyle}>{employee.role}</span>}
      withLeftAccessory={<Avatar user={employee} />}
    />
  );

  return (
    <Box ref={containerRef} style={{display: 'inline-block', width: '100%'}}>
      {/* Selected members as chips */}
      {selections.length > 0 && (
        <Box display="flex" style={{flexWrap: 'wrap', gap: 4, marginBottom: 4}} alignItems="center">
          {selections.map((employee) => (
            <EmployeeChip
              key={employee.user_id}
              employee={employee}
              handleChipClick={disabled ? () => handleOpenProfile(employee) : undefined}
              handleDelete={disabled ? undefined : () => handleRemove(employee)}
              isSelected={isChipSelectionActive}
            />
          ))}
          <Tooltip title={t('copy_emails')}>
            <IconButton variant="borderless" size="small" onClick={handleCopyEmails}>
              <UIIcon src={CopyLine} size="small" />
            </IconButton>
          </Tooltip>
          {!disabled && !keepInputVisible && !isInputVisible && (
            <Tooltip title={t('search_dropboxers')}>
              <IconButton
                variant="borderless"
                size="small"
                onClick={() => {
                  shouldFocusInputRef.current = true;
                  setIsInputVisible(true);
                }}
              >
                <UIIcon src={SearchLine} size="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      {!disabled && isInputVisible && (
        <Box className={inputClassName} marginTop={selections.length > 0 ? '12' : undefined}>
          <Typeahead.Wrapper
            wrapperRef={typeaheadWrapperRef}
            onSelection={handleSelection}
            onToggle={handleTypeaheadToggle}
            isPortaled
            closeOnSelection={false}
            openMenuOnFocus={autoFocus}
          >
            {({getTriggerProps, getContentProps, openMenu}) => (
              <>
                <TextInput
                  name="team-member-typeahead"
                  autoFocus={autoFocus}
                  size="small"
                  placeholder={t('search_dropboxers')}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  value={userInputValue}
                  withLeftAccessory={
                    <UIIcon src={SearchLine} className={atoms({color: 'Text Subtle'})} />
                  }
                  {...getTriggerProps({
                    onChange: onInputChange,
                    onClick: () => openMenu({autoFocus: false}),
                    onKeyDown,
                  })}
                />
                {filteredHints.length > 0 && (
                  <Typeahead.Container
                    {...getContentProps()}
                    auto={resultsAuto}
                    placement={resultsPlacement}
                    style={{width: resultsWidth, ...resultsContainerStyle}}
                  >
                    <Typeahead.Results
                      initialResults={filteredHints.length}
                      results={filteredHints}
                      renderRow={renderTypeaheadRow}
                    />
                  </Typeahead.Container>
                )}
              </>
            )}
          </Typeahead.Wrapper>
        </Box>
      )}
    </Box>
  );
};

const EmployeeTruncatedName = ({name, maxWidth}: {name: string; maxWidth: number}) => {
  const [shouldShowTooltip, setShouldShowTooltip] = useState(false);

  const hideName = maxWidth <= 0;
  const truncatedName = (
    <Box
      style={{
        display: hideName ? 'none' : 'block',
        maxWidth: hideName ? undefined : `${maxWidth}px`,
      }}
      onMouseLeave={() => setShouldShowTooltip(false)}
    >
      <Truncate onTooltipShown={() => setShouldShowTooltip(true)}>{name}</Truncate>
    </Box>
  );

  if (shouldShowTooltip) {
    return (
      <Tooltip title={name} placement="bottom">
        {truncatedName}
      </Tooltip>
    );
  }
  return truncatedName;
};

const EmployeeTypeahead = ({
  isInvalid,
  selection,
  placeholder,
  onSingleSelect,
  autoFocus = true,
  disabled = false,
  resultsWidth = 400,
  resultsContainerStyle,
  onClear,
}: {
  isInvalid?: boolean;
  selection?: EmployeeLdap;
  placeholder: Employee[];
  onSingleSelect?: (employee: Employee) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  resultsWidth?: number | string;
  resultsContainerStyle?: React.CSSProperties;
  onClear?: () => void;
}) => {
  const typeaheadWrapperRef = useRef<WrapperRef>();
  const [userInputValue, setUserInputValue] = useState(selection?.name ?? '');
  const searchResult = useSearches({
    ldaps: userInputValue.split(',').map((e) => (!e.includes('@') ? e : e.trim().split('@')[0])),
  });

  const employeeHints = searchResult.length ? searchResult : placeholder;

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUserInputValue(event.currentTarget.value);
  };

  // Stop propagation for Cmd/Ctrl+A to allow native select-all in input
  // This prevents Lexical from intercepting the event when inside a decorator node
  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if ((event.key === 'a' || event.key === 'A') && (event.metaKey || event.ctrlKey)) {
      event.stopPropagation();
    }
  };

  const handleSelection = (userId: string) => {
    const selectedEmployee = employeeHints.find((e) => e.user_id === userId);
    if (selectedEmployee) {
      onSingleSelect?.(selectedEmployee);
      setUserInputValue('');
    }
  };

  const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onClear?.();
    setUserInputValue('');
  };

  const renderTypeaheadRow = (employee: Employee) => (
    <Typeahead.Row
      key={employee.user_id}
      value={employee.user_id}
      withTitle={<span style={employeeTypeaheadTextStyle}>{employee.name}</span>}
      withSubtitle={<span style={employeeTypeaheadTextStyle}>{employee.role}</span>}
      withLeftAccessory={<Avatar user={employee} />}
    />
  );

  useEffect(() => {
    if (!autoFocus || disabled) return;

    const frameId = requestAnimationFrame(() => {
      typeaheadWrapperRef.current?.focusTrigger();
      typeaheadWrapperRef.current?.openResults({autoFocus: false});
    });

    return () => cancelAnimationFrame(frameId);
  }, [autoFocus, disabled]);

  if (disabled && selection) {
    return <EmployeeButton employee={selection} />;
  }

  return (
    <Typeahead.Wrapper
      wrapperRef={typeaheadWrapperRef}
      onSelection={handleSelection}
      isPortaled
      openMenuOnFocus={autoFocus && !disabled}
    >
      {({getTriggerProps, getContentProps, openMenu}) => (
        <>
          <TextInput
            name="employee-typeahead"
            isInvalid={isInvalid}
            disabled={disabled}
            autoFocus={autoFocus && !disabled}
            isTransparent
            size="small"
            placeholder={t('search_dropboxers')}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            value={userInputValue}
            withLeftAccessory={
              <UIIcon src={SearchLine} className={atoms({color: 'Text Subtle'})} />
            }
            withRightAccessory={
              onClear && !disabled ? (
                <IconButton
                  aria-label="Clear"
                  variant="borderless"
                  size="small"
                  onClick={handleClear}
                >
                  <UIIcon src={CloseLine} size="small" />
                </IconButton>
              ) : undefined
            }
            {...getTriggerProps({
              onChange: onInputChange,
              onClick: () => {
                if (!disabled) {
                  openMenu({autoFocus: false});
                }
              },
              onKeyDown,
            })}
          />

          <Typeahead.Container
            {...getContentProps()}
            elementResize
            style={{width: resultsWidth, ...resultsContainerStyle}}
          >
            <Typeahead.Results
              initialResults={employeeHints.length}
              results={employeeHints}
              renderRow={renderTypeaheadRow}
            />
          </Typeahead.Container>
        </>
      )}
    </Typeahead.Wrapper>
  );
};
